/*
 * A deliberately small WebGL2 renderer: two programs (lit surfaces, additive
 * edges), one VAO pair per mesh, one draw pair per part. The canvas is
 * transparent so the CSS blueprint background and glow show through.
 */

import { Mat4, Vec3 } from './math';
import { MeshData } from './geometry';
import { EDGE_FS, EDGE_VS, SURFACE_FS, SURFACE_VS } from './shaders';

export interface Material {
  base: Vec3;
  emissive?: Vec3;
  spec: number;
  gloss: number;
  metal: number;
  rim: number;
}

export interface GpuMesh {
  vao: WebGLVertexArrayObject;
  count: number;
  edgeVao: WebGLVertexArrayObject | null;
  edgeCount: number;
}

export interface Draw {
  mesh: GpuMesh;
  model: Mat4;
  material: Material;
  reveal: number;
  edge: number;
  grow: number;
}

export interface FrameGlobals {
  viewProj: Mat4;
  eye: Vec3;
  keyDir: Vec3;
  sky: Vec3;
  ground: Vec3;
  edgeCol: Vec3;
  scanX: number;
  scan: number;
}

const SURFACE_UNIFORMS = [
  'uViewProj', 'uModel', 'uEye', 'uKeyDir', 'uSky', 'uGround', 'uEdgeCol', 'uBase', 'uEmissive',
  'uSpec', 'uGloss', 'uMetal', 'uRim', 'uReveal', 'uGrow', 'uScanX', 'uScan',
] as const;
const EDGE_UNIFORMS = ['uViewProj', 'uModel', 'uEdgeCol', 'uEdge', 'uGrow', 'uScanX', 'uScan'] as const;

type Uniforms<K extends readonly string[]> = Record<K[number], WebGLUniformLocation | null>;

function compile(gl: WebGL2RenderingContext, vs: string, fs: string) {
  const make = (type: number, src: string) => {
    const s = gl.createShader(type);
    if (!s) throw new Error('createShader failed');
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || 'shader compile failed');
    return s;
  };
  const p = gl.createProgram();
  if (!p) throw new Error('createProgram failed');
  gl.attachShader(p, make(gl.VERTEX_SHADER, vs));
  gl.attachShader(p, make(gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) || 'program link failed');
  return p;
}

function locate<K extends readonly string[]>(gl: WebGL2RenderingContext, p: WebGLProgram, names: K): Uniforms<K> {
  const out = {} as Uniforms<K>;
  for (const n of names) (out as Record<string, WebGLUniformLocation | null>)[n] = gl.getUniformLocation(p, n);
  return out;
}

export class Renderer {
  readonly gl: WebGL2RenderingContext;
  private surf: WebGLProgram;
  private edge: WebGLProgram;
  private su: Uniforms<typeof SURFACE_UNIFORMS>;
  private eu: Uniforms<typeof EDGE_UNIFORMS>;
  private buffers: WebGLBuffer[] = [];
  private vaos: WebGLVertexArrayObject[] = [];

  /** Throws if WebGL2 is unavailable or the shaders fail - callers fall back to 2D. */
  constructor(readonly canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', {
      antialias: true,
      alpha: true,
      premultipliedAlpha: true,
      powerPreference: 'high-performance',
    });
    if (!gl) throw new Error('WebGL2 unavailable');
    this.gl = gl;
    this.surf = compile(gl, SURFACE_VS, SURFACE_FS);
    this.edge = compile(gl, EDGE_VS, EDGE_FS);
    this.su = locate(gl, this.surf, SURFACE_UNIFORMS);
    this.eu = locate(gl, this.edge, EDGE_UNIFORMS);
  }

  private buffer(target: number, data: ArrayBufferView) {
    const gl = this.gl;
    const b = gl.createBuffer();
    if (!b) throw new Error('createBuffer failed');
    gl.bindBuffer(target, b);
    gl.bufferData(target, data, gl.STATIC_DRAW);
    this.buffers.push(b);
    return b;
  }

  upload(m: MeshData): GpuMesh {
    const gl = this.gl;
    const vao = gl.createVertexArray();
    if (!vao) throw new Error('createVertexArray failed');
    this.vaos.push(vao);
    gl.bindVertexArray(vao);
    const attr = (loc: number, data: Float32Array, size: number) => {
      this.buffer(gl.ARRAY_BUFFER, data);
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
    };
    attr(0, m.pos, 3);
    attr(1, m.nrm, 3);
    attr(2, m.data, 2);
    this.buffer(gl.ELEMENT_ARRAY_BUFFER, m.idx);
    gl.bindVertexArray(null);

    let edgeVao: WebGLVertexArrayObject | null = null;
    if (m.edges.length) {
      edgeVao = gl.createVertexArray();
      if (!edgeVao) throw new Error('createVertexArray failed');
      this.vaos.push(edgeVao);
      gl.bindVertexArray(edgeVao);
      attr(0, m.edges, 4);
      gl.bindVertexArray(null);
    }
    return { vao, count: m.idx.length, edgeVao, edgeCount: m.edges.length / 4 };
  }

  resize(cssW: number, cssH: number, dpr: number) {
    const w = Math.max(1, Math.round(cssW * dpr));
    const h = Math.max(1, Math.round(cssH * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.gl.viewport(0, 0, w, h);
  }

  frame(g: FrameGlobals, draws: Draw[]) {
    const gl = this.gl;
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.disable(gl.CULL_FACE);

    // Surfaces.
    gl.useProgram(this.surf);
    gl.disable(gl.BLEND);
    gl.depthMask(true);
    gl.enable(gl.POLYGON_OFFSET_FILL);
    gl.polygonOffset(1, 1);
    const su = this.su;
    gl.uniformMatrix4fv(su.uViewProj, false, g.viewProj);
    gl.uniform3fv(su.uEye, g.eye);
    gl.uniform3fv(su.uKeyDir, g.keyDir);
    gl.uniform3fv(su.uSky, g.sky);
    gl.uniform3fv(su.uGround, g.ground);
    gl.uniform3fv(su.uEdgeCol, g.edgeCol);
    gl.uniform1f(su.uScanX, g.scanX);
    gl.uniform1f(su.uScan, g.scan);
    const zero: Vec3 = [0, 0, 0];
    for (const d of draws) {
      if (d.reveal <= 0.001 || d.grow <= 0.001) continue;
      const mt = d.material;
      gl.uniformMatrix4fv(su.uModel, false, d.model);
      gl.uniform3fv(su.uBase, mt.base);
      gl.uniform3fv(su.uEmissive, mt.emissive ?? zero);
      gl.uniform1f(su.uSpec, mt.spec);
      gl.uniform1f(su.uGloss, mt.gloss);
      gl.uniform1f(su.uMetal, mt.metal);
      gl.uniform1f(su.uRim, mt.rim);
      gl.uniform1f(su.uReveal, d.reveal);
      gl.uniform1f(su.uGrow, d.grow);
      gl.bindVertexArray(d.mesh.vao);
      gl.drawElements(gl.TRIANGLES, d.mesh.count, gl.UNSIGNED_INT, 0);
    }
    gl.disable(gl.POLYGON_OFFSET_FILL);

    // Edges: additive, depth-tested, no depth writes. Alpha is left alone so
    // lines over the transparent background composite as pure added light.
    gl.useProgram(this.edge);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.ONE, gl.ONE, gl.ZERO, gl.ONE);
    gl.depthMask(false);
    const eu = this.eu;
    gl.uniformMatrix4fv(eu.uViewProj, false, g.viewProj);
    gl.uniform3fv(eu.uEdgeCol, g.edgeCol);
    gl.uniform1f(eu.uScanX, g.scanX);
    gl.uniform1f(eu.uScan, g.scan);
    for (const d of draws) {
      if (!d.mesh.edgeVao || d.edge <= 0.001 || d.grow <= 0.001) continue;
      gl.uniformMatrix4fv(eu.uModel, false, d.model);
      gl.uniform1f(eu.uEdge, d.edge);
      gl.uniform1f(eu.uGrow, d.grow);
      gl.bindVertexArray(d.mesh.edgeVao);
      gl.drawArrays(gl.LINES, 0, d.mesh.edgeCount);
    }
    gl.bindVertexArray(null);
    gl.depthMask(true);
  }

  dispose() {
    const gl = this.gl;
    this.vaos.forEach(v => gl.deleteVertexArray(v));
    this.buffers.forEach(b => gl.deleteBuffer(b));
    gl.deleteProgram(this.surf);
    gl.deleteProgram(this.edge);
  }
}
